import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { EditButton, SortButton, TableHeader, sortList } from '../../common/Table';
import SortIcon from '../../img/sort.svg';

const BookletSeriesRow = ({
  series,
  onEditSeries,
  onAddBooklet,
  onEditPosts,
  onDeleteSeries,
  renderBookletRow
}) => {
  const {
    _id,
    name,
    logoFullURL,
    booklets = [],
    totalSoldBooklets,
    colorLeft,
    colorRight,
    colorFont,
    colorSalesMaterial,
    price
  } = series;

  const [sortBy, setSortBy] = useState('validFrom');
  const [sortOrder, setSortOrder] = useState(-1);
  const sort = { sortBy, sortOrder, setSortBy, setSortOrder };

  const bookletCount = booklets.length;
  const usedOrders = booklets.reduce((sum, b) => sum + (b.usedOrders || 0), 0);

  // Detect overlapping periods between booklets in this series
  const getOverlaps = () => {
    const overlaps = {};
    for (let i = 0; i < booklets.length; i++) {
      for (let j = i + 1; j < booklets.length; j++) {
        const a = booklets[i];
        const b = booklets[j];

        const validOverlap = a.validFrom && a.validTo && b.validFrom && b.validTo &&
          new Date(a.validFrom) < new Date(b.validTo) &&
          new Date(b.validFrom) < new Date(a.validTo);

        const salesOverlap = a.buyableFrom && a.buyableTo && b.buyableFrom && b.buyableTo &&
          new Date(a.buyableFrom) < new Date(b.buyableTo) &&
          new Date(b.buyableFrom) < new Date(a.buyableTo);

        if (validOverlap) {
          overlaps[a._id] = { ...overlaps[a._id], valid: true };
          overlaps[b._id] = { ...overlaps[b._id], valid: true };
        }
        if (salesOverlap) {
          overlaps[a._id] = { ...overlaps[a._id], sales: true };
          overlaps[b._id] = { ...overlaps[b._id], sales: true };
        }
      }
    }
    return overlaps;
  };

  const overlaps = bookletCount > 1 ? getOverlaps() : {};

  const colorSwatch = (color, title) => (
    <div
      title={title}
      style={{
        backgroundColor: color,
        width: 20,
        height: 20,
        borderRadius: 3,
        border: '1px solid rgba(0,0,0,0.1)'
      }}
    ></div>
  );

  const seriesSubHeaderColumns = [
    { label: '', style: { width: 10 } },
    { label: 'Logga', className: 'centered-logo' },
    { label: 'Häfte', sortField: 'title' },
    { label: 'Säljperiod', sortField: 'buyableFrom' },
    { label: 'Giltig', sortField: 'validFrom' },
    { label: 'Färger' },
    { label: 'Pris (kr)' },
    { label: 'Akt./Sålda', className: 'booklet-sold' },
    { label: '', colSpan: 7 },
  ];

  return (
    <>
      {/* Series Badge Row */}
      <tr className="booklet-series-badge-row">
        <td colSpan="9">
          <span className="series-badge">Serie</span>
          <span style={{ fontSize: '0.75rem', color: 'rgba(0,0,0,0.4)', marginLeft: '0.5rem' }}>
            ({bookletCount} {bookletCount === 1 ? 'häfte' : 'häften'})
          </span>
        </td>
      </tr>

      {/* Series Banner */}
      <tr className="booklet-series-banner">
        <td></td>
        <td className="centered-logo">
          {logoFullURL && (
            <img src={logoFullURL} alt={name} style={{ maxWidth: 100, maxHeight: 60, objectFit: 'contain' }} />
          )}
        </td>
        <td colSpan="3">
          <strong style={{ fontSize: '1.05rem' }}>{name}</strong>
        </td>
        <td>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3, width: 'fit-content' }}>
            {colorLeft && colorSwatch(colorLeft, 'Färg vänster')}
            {colorRight && colorSwatch(colorRight, 'Färg höger')}
            {colorFont && colorSwatch(colorFont, 'Färg text')}
            {colorSalesMaterial && colorSwatch(colorSalesMaterial, 'Färg försäljningsmaterial')}
          </div>
        </td>
        <td>{price != null ? price : ''}</td>
        <td className="booklet-sold">{bookletCount > 0 ? `${usedOrders}/${totalSoldBooklets}` : ''}</td>
        <td style={{ textAlign: 'right' }}>
          <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <EditButton size="xs" setEditing={() => onEditSeries(_id)}>
              Redigera serie
            </EditButton>
            <button className="button secondary xs" onClick={() => onAddBooklet(_id)}>
              Lägg till häfte
            </button>
            <button className="button secondary xs" onClick={() => onEditPosts(_id)}>
              Inlägg
            </button>
            {bookletCount === 0 && (
              <button className="button secondary xs" onClick={() => onDeleteSeries(_id)} style={{ color: '#c0392b' }}>
                Ta bort serie
              </button>
            )}
          </div>
        </td>
      </tr>

      {/* Sub-header for booklet columns */}
      {booklets.length > 0 && (
        <TableHeader columns={seriesSubHeaderColumns} sort={sort} variant="sub-header" />
      )}

      {/* Booklets */}
      {booklets.length > 0 && (
        sortList([...booklets], sort)
          .map(booklet => {
            const tag = series.currentBooklet?._id === booklet._id
              ? 'NU'
              : series.nextBooklet?._id === booklet._id
                ? 'NÄSTA'
                : null;
            return (
              <React.Fragment key={booklet._id}>
                {renderBookletRow(booklet, { isNested: true, tag, overlaps: overlaps[booklet._id] })}
              </React.Fragment>
            );
          })
      )}

      {/* Empty state */}
      {booklets.length === 0 && (
        <tr className="booklet-series-empty">
          <td colSpan="9" style={{ padding: '1rem', textAlign: 'center', opacity: 0.6 }}>
            Inga häften i denna serie än
          </td>
        </tr>
      )}
    </>
  );
};

BookletSeriesRow.propTypes = {
  series: PropTypes.object.isRequired,
  onEditSeries: PropTypes.func.isRequired,
  onAddBooklet: PropTypes.func.isRequired,
  onEditPosts: PropTypes.func.isRequired,
  onDeleteSeries: PropTypes.func.isRequired,
  renderBookletRow: PropTypes.func.isRequired,
};

export default BookletSeriesRow;
